import * as THREE from 'three';

const FONT = '"Google Sans Flex","Google Sans","Noto Sans",Arial,sans-serif';

/** Écran dynamique : une seule toile, plusieurs textures (ex. moitiés du pliable).
 *  Modes : lock | zoom | charge | gemini | custom image. */
export class DynamicScreen {
  constructor({ wide = false, clock = '11:00', date = 'ven. 16 oct.' } = {}) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = wide ? 1400 : 700; this.canvas.height = 1400;
    this.wide = wide; this.clock = clock; this.date = date;
    this.textures = [];
    this.mode = 'lock'; this.params = {};
    this.t = 0;
    this.draw('lock');
    // redessine quand Google Sans est chargée (le canvas ne se met pas à jour seul)
    document.fonts?.load?.(`400 100px ${FONT}`).then(() => this.draw(this.mode, this.params)).catch(() => {});
  }
  texture(repeat = [1, 1], offset = [0, 0]) {
    const t = new THREE.CanvasTexture(this.canvas);
    t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8;
    t.repeat.set(...repeat); t.offset.set(...offset);
    this.textures.push(t); return t;
  }
  set(mode, params = {}) { this.mode = mode; this.params = params; this.t = 0; this.draw(mode, params); }
  /** appelé à chaque frame pour les modes animés */
  tick(dt) {
    if (this.mode === 'charge' || this.mode === 'gemini' || this.mode === 'zoom') {
      this.t += dt; this.draw(this.mode, this.params);
    }
  }
  _flush() { this.textures.forEach(t => (t.needsUpdate = true)); }

  draw(mode, p = {}) {
    const c = this.canvas, g = c.getContext('2d'), W = c.width, H = c.height;
    const cx = this.wide ? W * .75 : W / 2;
    g.save(); g.clearRect(0, 0, W, H);
    if (mode === 'lock') {
      const gr = g.createLinearGradient(0, 0, W * .3, H);
      gr.addColorStop(0, '#0d1424'); gr.addColorStop(.55, '#26344a'); gr.addColorStop(1, '#556a72');
      g.fillStyle = gr; g.fillRect(0, 0, W, H);
      ['#2f4157', '#46607a', '#7d97a1', '#c7d3cf', '#a8b9b6'].forEach((col, i) => {
        g.beginPath(); g.moveTo(0, H * (.45 + i * .1));
        for (let x = 0; x <= W; x += 20) g.lineTo(x, H * (.45 + i * .1) + Math.sin(x / W * Math.PI * (1.4 + i * .3) + i) * H * .06);
        g.lineTo(W, H); g.lineTo(0, H); g.closePath(); g.globalAlpha = .55; g.fillStyle = col; g.fill();
      });
      g.globalAlpha = 1; g.fillStyle = 'rgba(255,255,255,.95)'; g.textAlign = 'center';
      const [hh, mm] = this.clock.split(':');
      g.font = `400 ${this.wide ? 260 : 230}px ${FONT}`; g.fillText(hh, cx, H * .2); g.fillText(mm, cx, H * .2 + (this.wide ? 240 : 215));
      g.font = `400 42px ${FONT}`; g.fillText(this.date, cx, H * .2 + (this.wide ? 330 : 300));
    }
    if (mode === 'zoom') {
      // scène « paysage » zoomée progressivement, avec sélecteur de zoom façon appli Photo
      const levels = p.levels || [1, 5, 30, 120];
      const period = (p.stepMs || 1400) / 1000;
      const idx = Math.min(levels.length - 1, Math.floor(this.t / period) % (levels.length + 1));
      const lv = levels[Math.min(idx, levels.length - 1)];
      const s = 1 + Math.log2(lv) * .9;
      g.save(); g.translate(W * .62, H * .38); g.scale(s, s); g.translate(-W * .62, -H * .38);
      const sky = g.createLinearGradient(0, 0, 0, H); sky.addColorStop(0, '#1f2a44'); sky.addColorStop(.6, '#e98a6b'); sky.addColorStop(1, '#f5c48a');
      g.fillStyle = sky; g.fillRect(0, 0, W, H);
      g.fillStyle = '#ffe7b0'; g.beginPath(); g.arc(W * .62, H * .38, 34, 0, 7); g.fill();
      g.fillStyle = '#2c2f3d'; g.beginPath(); g.moveTo(0, H * .72);
      [[.15, .6], [.3, .68], [.45, .55], [.6, .66], [.75, .58], [.9, .64], [1, .6]].forEach(([x, y]) => g.lineTo(W * x, H * y)); g.lineTo(W, H); g.lineTo(0, H); g.fill();
      g.fillStyle = '#171923'; g.fillRect(W * .61, H * .42, 6, 40); g.fillRect(W * .6, H * .41, 22, 8);
      g.restore();
      // UI
      g.fillStyle = 'rgba(0,0,0,.35)'; g.fillRect(0, H * .86, W, H * .14);
      const bw = 110, gap = 18, total = levels.length * bw + (levels.length - 1) * gap; let x0 = (this.wide ? W * .75 : W / 2) - total / 2;
      levels.forEach((l, i) => {
        g.fillStyle = l === lv ? '#fff' : 'rgba(255,255,255,.18)';
        g.beginPath(); g.roundRect(x0 + i * (bw + gap), H * .9, bw, 64, 32); g.fill();
        g.fillStyle = l === lv ? '#111' : '#fff'; g.font = `600 34px ${FONT}`; g.textAlign = 'center';
        g.fillText(l + 'x', x0 + i * (bw + gap) + bw / 2, H * .9 + 44);
      });
    }
    if (mode === 'charge') {
      g.fillStyle = '#05070b'; g.fillRect(0, 0, W, H);
      const pct = Math.min(100, (p.from ?? 20) + this.t * (p.speed ?? 12));
      const r = Math.min(W, H) * .22;
      g.lineWidth = 26; g.strokeStyle = 'rgba(255,255,255,.12)'; g.beginPath(); g.arc(cx, H * .42, r, 0, 7); g.stroke();
      const gr = g.createLinearGradient(cx - r, 0, cx + r, 0); gr.addColorStop(0, '#34a853'); gr.addColorStop(1, '#a8e6b5');
      g.strokeStyle = gr; g.lineCap = 'round'; g.beginPath(); g.arc(cx, H * .42, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * pct / 100); g.stroke();
      g.fillStyle = '#fff'; g.textAlign = 'center'; g.font = `500 140px ${FONT}`; g.fillText(Math.round(pct) + '%', cx, H * .42 + 50);
      g.font = `400 44px ${FONT}`; g.fillStyle = 'rgba(255,255,255,.8)'; g.fillText(p.label || 'Recharge sans fil · 25 W', cx, H * .72);
    }
    if (mode === 'gemini') {
      g.fillStyle = '#0b0c10'; g.fillRect(0, 0, W, H);
      const a = .6 + .4 * Math.sin(this.t * 3);
      const gr = g.createRadialGradient(cx, H * 1.05, 10, cx, H * 1.05, H * .6);
      gr.addColorStop(0, `rgba(66,133,244,${.9 * a})`); gr.addColorStop(.4, `rgba(155,114,203,${.6 * a})`); gr.addColorStop(1, 'rgba(217,101,112,0)');
      g.fillStyle = gr; g.fillRect(0, 0, W, H);
      g.fillStyle = '#fff'; g.textAlign = 'center'; g.font = `400 58px ${FONT}`; g.fillText(p.text || 'Je vous écoute…', cx, H * .45);
    }
    g.restore();
    this._flush();
  }
}
